import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import Tooltip from '@srm/ui/tooltip';
import dayjs from '@srm/ui/lib/dayjs';
import { formatDetailedDateTime } from '@/utils/chatDateTime';
import { IConversationListItem } from '@/models/chatai/IConversationListItem';
import { ConversationItemMenu } from './ConversationItemMenu';

interface IProps {
    item: IConversationListItem;
    isActive: boolean;
    isRenaming: boolean;
    onSelect: (conversationId: string) => void;
    onStartRename: (conversationId: string) => void;
    onCommitRename: (conversationId: string, newTitle: string) => void;
    onCancelRename: () => void;
    onDelete: (conversationId: string) => void;
}

const FALLBACK_TITLE = 'Без названия';
const MAX_TITLE = 120;

/** memo: обработчики получают id треда, а не замыкание строки — обновление списка рисует только изменившиеся пункты. */
export const ConversationListItem = React.memo(function ConversationListItem({
    item,
    isActive,
    isRenaming,
    onSelect: onSelectId,
    onStartRename: onStartRenameId,
    onCommitRename: onCommitRenameId,
    onCancelRename,
    onDelete: onDeleteId,
}: IProps) {
    const id = item.conversationId;
    const onSelect = () => onSelectId(id);
    const onStartRename = () => onStartRenameId(id);
    const onCommitRename = (newTitle: string) => onCommitRenameId(id, newTitle);
    const onDelete = () => onDeleteId(id);
    const title = item.title?.trim() || FALLBACK_TITLE;
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isRenaming && inputRef.current) {
            const el = inputRef.current;
            el.focus();
            el.select();
        }
    }, [isRenaming]);

    // «Создан» показываем, только если он ощутимо раньше последнего изменения —
    // у только что созданного треда с одним сообщением обе даты совпадают.
    const showCreated = !dayjs(item.createdAt).isSame(item.updatedAt, 'minute');
    const tooltipLabel = (
        <div className="chat-ai-conv-tooltip">
            <div className="chat-ai-conv-tooltip__title">{title}</div>
            <div className="chat-ai-conv-tooltip__meta">
                Изменён: {formatDetailedDateTime(item.updatedAt)}
            </div>
            {showCreated && (
                <div className="chat-ai-conv-tooltip__meta">
                    Создан: {formatDetailedDateTime(item.createdAt)}
                </div>
            )}
        </div>
    );

    return (
        <Tooltip
            background="black"
            wrapTarget={false}
            interactive={false}
            maxWidth={260}
            position="right"
            openDelay={400}
            zIndex={10_000}
            // над открытым меню «Действия» строки подсказка не висит сама (кнопка меню — внутри строки)
            disabled={isRenaming}
            label={tooltipLabel}
        >
        <div
            className={clsx('chat-ai-sidebar__item', {
                'chat-ai-sidebar__item--active': isActive,
                'chat-ai-sidebar__item--editing': isRenaming,
            })}
            role={isRenaming ? undefined : 'button'}
            tabIndex={isRenaming ? -1 : 0}
            onClick={isRenaming ? undefined : onSelect}
            onKeyDown={(e) => {
                if (isRenaming) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                } else if (e.key === 'F2') {
                    e.preventDefault();
                    onStartRename();
                }
            }}
        >
            {isRenaming ? (
                <input
                    ref={inputRef}
                    type="text"
                    defaultValue={item.title ?? ''}
                    maxLength={MAX_TITLE}
                    placeholder="Название чата"
                    className="chat-ai-sidebar__item-input"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onCommitRename(e.currentTarget.value);
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            onCancelRename();
                        }
                    }}
                    onBlur={(e) => onCommitRename(e.currentTarget.value)}
                />
            ) : (
                <>
                    <span className="chat-ai-sidebar__item-title">{title}</span>
                    {item.mode === 'agent' && (
                        <span
                            className="chat-ai-sidebar__item-mode-badge"
                            title="AI-агент"
                            aria-label="Режим: агент"
                        >
                            Агент
                        </span>
                    )}
                </>
            )}
            {!isRenaming && (
                <ConversationItemMenu onRename={onStartRename} onDelete={onDelete} />
            )}
        </div>
        </Tooltip>
    );
});
